import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable()
export class BrowserStorageService {
  private storeName = 'appData';
  private sessionStorageSubject = new BehaviorSubject<Record<string, unknown>>(this.loadInitialData(true));
  private localStorageSubject = new BehaviorSubject<Record<string, unknown>>(this.loadInitialData(false));

  private safeParse(raw: string | null): Record<string, unknown> {
    try {
      const parsed = JSON.parse(raw ?? '{}');
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }

  private loadInitialData(isSessionStorage: boolean): Record<string, unknown> {
    return this.safeParse(
      isSessionStorage ? sessionStorage.getItem(this.storeName) : localStorage.getItem(this.storeName)
    );
  }

  private ensureObject(data: unknown): Record<string, unknown> {
    return typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {};
  }

  public initSessionStorage(): void {
    const store = this.ensureObject(this.sessionStorageSubject.value);
    this.sessionStorageSubject.next(store);
    sessionStorage.setItem(this.storeName, JSON.stringify(store));
  }

  public initLocalStorage(): void {
    const store = this.ensureObject(this.localStorageSubject.value);
    this.localStorageSubject.next(store);
    localStorage.setItem(this.storeName, JSON.stringify(store));
  }

  public setSessionStorageData(category: string, data: unknown): void {
    const store = this.ensureObject(this.sessionStorageSubject.value);
    store[category] = data;
    this.sessionStorageSubject.next(store);
    sessionStorage.setItem(this.storeName, JSON.stringify(store));
  }

  public setLocalStorageData(category: string, data: unknown): void {
    const store = this.ensureObject(this.localStorageSubject.value);
    store[category] = data;
    this.localStorageSubject.next(store);
    localStorage.setItem(this.storeName, JSON.stringify(store));
  }

  public getItem(propertyValue: string, isSessionStorage = true): unknown {
    if (isSessionStorage) {
      return this.sessionStorageSubject.value[propertyValue];
    } else {
      return this.localStorageSubject.value[propertyValue];
    }
  }

  public itemChanges(): Observable<Record<string, unknown>> {
    return this.sessionStorageSubject.asObservable();
  }
}
